// =====================================================================
// Gestão clínica (Dashboard D1 / S88) — painel gerencial pro dono/médico.
// Consome /clinic-dashboard/* (escopo = clínica ativa). ApexCharts + MUI v4.
// Visual premium, dark-aware (cards com superfície/borda próprias — não usa
// só o Paper default que some no dark). Estilos 100% escopados (makeStyles),
// sem tocar MuiPaper global [[feedback_css_crm_planejamento]].
// =====================================================================
import React, { useEffect, useState, useCallback } from "react";
import Chart from "react-apexcharts";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Grid, Typography, CircularProgress, Box } from "@material-ui/core";
import {
  Event,
  HowToReg,
  EventBusy,
  AttachMoney,
  ConfirmationNumber,
  PersonAdd,
  TrendingUp,
  TrendingDown,
  EmojiObjects,
  Warning,
  AccessTime,
  Android
} from "@material-ui/icons";
import moment from "moment";

import * as svc from "../../services/clinicDashboardService";

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const surface = dark ? "#1a1b21" : "#ffffff";
  const surfaceAlt = dark ? "#15161b" : "#f7f8fa";
  const border = dark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #e9eaee";
  const borderHover = dark ? "1px solid rgba(255,255,255,0.18)" : "1px solid #d4d6dd";
  const shadow = dark ? "0 1px 2px rgba(0,0,0,0.4)" : "0 1px 3px rgba(16,24,40,0.06)";
  const textPrimary = dark ? "#f2f3f5" : "#1a1d23";
  const textMuted = dark ? "#8b8f9a" : "#6b7280";
  return {
    root: {
      padding: theme.spacing(3),
      overflowY: "auto",
      height: "calc(100vh - 48px)",
      background: dark ? "#0e0f13" : "#f4f5f7"
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 12,
      marginBottom: theme.spacing(3)
    },
    title: { fontWeight: 700, color: textPrimary, fontSize: 26, letterSpacing: "-0.5px" },
    subtitle: { color: textMuted, fontSize: 13, marginTop: 2 },
    segment: {
      display: "inline-flex",
      background: surfaceAlt,
      border,
      borderRadius: 10,
      padding: 3
    },
    segBtn: {
      border: "none",
      background: "transparent",
      color: textMuted,
      fontSize: 13,
      fontWeight: 600,
      padding: "7px 16px",
      borderRadius: 8,
      cursor: "pointer",
      transition: "all .15s",
      fontFamily: "inherit"
    },
    segBtnActive: { background: "#3b82f6", color: "#fff", boxShadow: "0 1px 3px rgba(59,130,246,0.4)" },
    kpi: {
      background: surface,
      border,
      borderRadius: 14,
      padding: "16px 18px",
      height: "100%",
      boxShadow: shadow,
      transition: "transform .15s, border-color .15s",
      "&:hover": { transform: "translateY(-2px)", border: borderHover }
    },
    kpiTop: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 },
    kpiLabel: { fontSize: 12.5, color: textMuted, fontWeight: 600, letterSpacing: "0.2px" },
    iconChip: { width: 34, height: 34, borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center" },
    kpiValue: { fontSize: 27, fontWeight: 700, lineHeight: 1.05, color: textPrimary },
    pill: { display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11.5, fontWeight: 700, padding: "2px 8px", borderRadius: 20, marginTop: 8 },
    pillUp: { background: dark ? "rgba(16,185,129,0.15)" : "#e7f7f0", color: "#10b981" },
    pillDown: { background: dark ? "rgba(239,68,68,0.15)" : "#fdecec", color: "#ef4444" },
    pillFlat: { color: textMuted, fontSize: 11.5, marginTop: 8, display: "inline-block" },
    card: {
      background: surface,
      border,
      borderRadius: 16,
      padding: theme.spacing(2.5),
      height: "100%",
      boxShadow: shadow
    },
    cardHead: { display: "flex", alignItems: "center", gap: 8, marginBottom: theme.spacing(2) },
    cardTitle: { fontSize: 15, fontWeight: 700, color: textPrimary },
    cardIcon: { color: "#3b82f6", fontSize: 19 },
    hero: { fontSize: 32, fontWeight: 700, lineHeight: 1.05, color: textPrimary, marginTop: 4 },
    muted: { fontSize: 12.5, color: textMuted },
    insightCard: {
      background: dark ? "linear-gradient(135deg, rgba(59,130,246,0.12), rgba(139,92,246,0.08))" : "linear-gradient(135deg, #eef4ff, #f5f0ff)",
      border: dark ? "1px solid rgba(99,102,241,0.25)" : "1px solid #dfe6fb",
      borderRadius: 16,
      padding: theme.spacing(2.5),
      height: "100%"
    },
    insight: { display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 10, fontSize: 13, color: textPrimary, lineHeight: 1.45 }
  };
});

const BRL = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 }).format(v || 0);

const PRESETS = [
  { key: "30d", label: "30 dias", range: () => ({ from: moment().subtract(29, "days").format("YYYY-MM-DD"), to: moment().format("YYYY-MM-DD") }) },
  { key: "mes", label: "Este mês", range: () => ({ from: moment().startOf("month").format("YYYY-MM-DD"), to: moment().format("YYYY-MM-DD") }) },
  { key: "ano", label: "Este ano", range: () => ({ from: moment().startOf("year").format("YYYY-MM-DD"), to: moment().format("YYYY-MM-DD") }) }
];

const Pill = ({ pct, pts, invert }) => {
  const c = useStyles();
  const val = pts !== undefined ? pts : pct;
  if (val === 0 || val === undefined || val === null) return <span className={c.pillFlat}>— estável</span>;
  const good = invert ? val < 0 : val > 0;
  const Icon = val > 0 ? TrendingUp : TrendingDown;
  const unit = pts !== undefined ? " pts" : "%";
  return (
    <span className={`${c.pill} ${good ? c.pillUp : c.pillDown}`}>
      <Icon style={{ fontSize: 13 }} /> {Math.abs(val)}{unit}
    </span>
  );
};

const Kpi = ({ label, value, icon, color, pill, foot }) => {
  const c = useStyles();
  return (
    <div className={c.kpi}>
      <div className={c.kpiTop}>
        <span className={c.kpiLabel}>{label}</span>
        <span className={c.iconChip} style={{ background: `${color}22`, color }}>{icon}</span>
      </div>
      <div className={c.kpiValue}>{value}</div>
      {pill}
      {foot}
    </div>
  );
};

const GestaoClinica = () => {
  const c = useStyles();
  const theme = useTheme();
  const isDark = theme.palette.type === "dark";
  const [preset, setPreset] = useState("mes");
  const [loading, setLoading] = useState(true);
  const [d, setD] = useState({});

  const load = useCallback(async (presetKey) => {
    setLoading(true);
    const { from, to } = PRESETS.find((p) => p.key === presetKey).range();
    try {
      const [summary, funnel, monthly, byDoctor, sources, ia, wait, demo, insights] = await Promise.all([
        svc.getSummary(from, to), svc.getFunnel(from, to), svc.getMonthly(), svc.getByDoctor(from, to),
        svc.getSources(from, to), svc.getIaVsHuman(from, to), svc.getWaitTime(from, to),
        svc.getDemographics(from, to), svc.getInsights(from, to)
      ]);
      setD({ summary, funnel, monthly, byDoctor, sources, ia, wait, demo, insights });
    } catch (e) {
      setD({ error: true });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(preset); }, [preset, load]);

  if (loading) {
    return <Box display="flex" justifyContent="center" alignItems="center" height="60vh"><CircularProgress /></Box>;
  }
  if (d.error) {
    return <div className={c.root}><Typography color="error">Não foi possível carregar o painel. Tente novamente.</Typography></div>;
  }

  const s = d.summary || {};
  const funnel = d.funnel || [];
  const byDoctor = d.byDoctor || [];
  const sources = d.sources || [];
  const ia = d.ia || { porOrigem: [] };
  const wait = d.wait || {};
  const demo = d.demo || {};
  const monthly = d.monthly || [];
  const insights = d.insights || [];

  const gridColor = isDark ? "rgba(255,255,255,0.07)" : "#eceef2";
  const axisColor = isDark ? "#8b8f9a" : "#6b7280";
  const baseChart = {
    chart: { toolbar: { show: false }, background: "transparent", fontFamily: "inherit", animations: { speed: 400 } },
    grid: { borderColor: gridColor, strokeDashArray: 3 },
    tooltip: { theme: isDark ? "dark" : "light" },
    theme: { mode: isDark ? "dark" : "light" }
  };
  const ax = { labels: { style: { colors: axisColor, fontSize: "12px" } } };

  const claraPct = (ia.porOrigem.find((x) => x.key === "clara_ia") || {}).pct ?? 0;
  const claraVal = (ia.porOrigem.find((x) => x.key === "clara_ia") || {}).valor ?? 0;

  return (
    <div className={c.root}>
      <div className={c.header}>
        <div>
          <div className={c.title}>Gestão clínica</div>
          <div className={c.subtitle}>Visão gerencial da clínica ativa · {moment().format("DD/MM/YYYY")}</div>
        </div>
        <div className={c.segment}>
          {PRESETS.map((p) => (
            <button key={p.key} className={`${c.segBtn} ${preset === p.key ? c.segBtnActive : ""}`} onClick={() => setPreset(p.key)}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPIs */}
      <Grid container spacing={2} style={{ marginBottom: 10 }}>
        <Grid item xs={6} sm={4} md={2}><Kpi label="Consultas" value={(s.consultas || {}).valor ?? 0} icon={<Event style={{ fontSize: 19 }} />} color="#3b82f6" pill={<Pill pct={(s.consultas || {}).deltaPct} />} /></Grid>
        <Grid item xs={6} sm={4} md={2}><Kpi label="Comparecimento" value={`${(s.comparecimentoPct || {}).valor ?? 0}%`} icon={<HowToReg style={{ fontSize: 19 }} />} color="#10b981" pill={<Pill pts={(s.comparecimentoPct || {}).deltaPts} />} /></Grid>
        <Grid item xs={6} sm={4} md={2}><Kpi label="No-show" value={`${(s.noShowPct || {}).valor ?? 0}%`} icon={<EventBusy style={{ fontSize: 19 }} />} color="#ef4444" pill={<Pill pts={(s.noShowPct || {}).deltaPts} invert />} foot={s.coberturaPct != null ? <span className={c.pillFlat} title="Só conta consultas passadas não-canceladas. Cobertura = quanto já foi fechado como comparecimento ou falta.">{s.coberturaPct}% desfechado{s.semDesfecho ? ` · ${s.semDesfecho} em aberto` : ""}</span> : null} /></Grid>
        <Grid item xs={6} sm={4} md={2}><Kpi label="Receita" value={BRL((s.receita || {}).valor)} icon={<AttachMoney style={{ fontSize: 19 }} />} color="#f59e0b" pill={<Pill pct={(s.receita || {}).deltaPct} />} /></Grid>
        <Grid item xs={6} sm={4} md={2}><Kpi label="Ticket médio" value={BRL((s.ticketMedio || {}).valor)} icon={<ConfirmationNumber style={{ fontSize: 19 }} />} color="#8b5cf6" pill={<Pill pct={(s.ticketMedio || {}).deltaPct} />} /></Grid>
        <Grid item xs={6} sm={4} md={2}><Kpi label="Pacientes novos" value={demo.novos ?? 0} icon={<PersonAdd style={{ fontSize: 19 }} />} color="#06b6d4" foot={<span className={c.pillFlat}>{demo.total ?? 0} no total</span>} /></Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* Funil */}
        <Grid item xs={12} md={6}>
          <div className={c.card}>
            <div className={c.cardHead}><EmojiObjects className={c.cardIcon} style={{ display: "none" }} /><span className={c.cardTitle}>Funil de atendimento</span></div>
            <Chart type="bar" height={250}
              series={[{ name: "Consultas", data: funnel.map((f) => f.valor) }]}
              options={{ ...baseChart,
                plotOptions: { bar: { horizontal: true, borderRadius: 6, distributed: true, barHeight: "60%" } },
                colors: ["#2563eb", "#3b82f6", "#60a5fa", "#93c5fd"],
                dataLabels: { enabled: true, style: { fontWeight: 700, colors: ["#fff"] } },
                legend: { show: false },
                xaxis: { categories: funnel.map((f) => f.etapa), ...ax },
                yaxis: { labels: { style: { colors: axisColor, fontSize: "13px", fontWeight: 600 } } },
                tooltip: { ...baseChart.tooltip, y: { formatter: (v, o) => `${v} (${(funnel[o.dataPointIndex] || {}).pct ?? 0}% do topo)` } }
              }} />
            {(funnel[0] || {}).canceladas > 0 && (
              <Typography className={c.muted} style={{ marginTop: 6, display: "block" }}>
                {(funnel[0] || {}).canceladas} canceladas no período — fora do funil (o topo conta só as não-canceladas).
              </Typography>
            )}
          </div>
        </Grid>

        {/* Como conheceu */}
        <Grid item xs={12} md={6}>
          <div className={c.card}>
            <div className={c.cardHead}><span className={c.cardTitle}>Como conheceu</span></div>
            {(() => {
              const naoInf = sources.find((x) => x.origem === "Não informado");
              const conhecidos = sources.filter((x) => x.origem !== "Não informado");
              const totalConhecidos = conhecidos.reduce((a, b) => a + b.valor, 0);
              const totalGeral = sources.reduce((a, b) => a + b.valor, 0);
              const MIN = 5;
              if (totalConhecidos < MIN) {
                return (
                  <Box display="flex" alignItems="center" justifyContent="center" height={250} flexDirection="column">
                    <PersonAdd style={{ fontSize: 36, color: axisColor, opacity: 0.4 }} />
                    <Typography className={c.muted} style={{ marginTop: 8, textAlign: "center", maxWidth: 300 }}>
                      A origem começa a aparecer conforme a assistente vai perguntando "como você chegou até a clínica" na triagem.{naoInf ? ` ${naoInf.valor} pacientes do período ainda sem origem registrada.` : ""}
                    </Typography>
                  </Box>
                );
              }
              return (
                <React.Fragment>
                  <Chart type="donut" height={230}
                    series={conhecidos.map((x) => x.valor)}
                    options={{ ...baseChart,
                      labels: conhecidos.map((x) => x.origem),
                      colors: ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ef4444", "#ec4899", "#f97316", "#64748b"],
                      legend: { position: "bottom", labels: { colors: axisColor }, fontSize: "12px" },
                      stroke: { width: 2, colors: [isDark ? "#1a1b21" : "#fff"] },
                      plotOptions: { pie: { donut: { size: "68%", labels: { show: true, total: { show: true, label: "Com origem", color: axisColor, formatter: () => totalConhecidos } } } } },
                      dataLabels: { enabled: true, formatter: (v, o) => `${(conhecidos[o.seriesIndex] || {}).valor ?? ""}`, style: { fontWeight: 700 } } }} />
                  <Typography className={c.muted} style={{ marginTop: 4, display: "block", textAlign: "center" }}>
                    {totalConhecidos} de {totalGeral} pacientes do período com origem registrada
                  </Typography>
                </React.Fragment>
              );
            })()}
          </div>
        </Grid>

        {/* Mês a mês */}
        <Grid item xs={12} md={6}>
          <div className={c.card}>
            <div className={c.cardHead}><span className={c.cardTitle}>Agendamentos mês a mês</span></div>
            <Chart type="area" height={220}
              series={[{ name: "Agendamentos", data: monthly.map((m) => m.valor) }]}
              options={{ ...baseChart,
                colors: ["#3b82f6"],
                stroke: { curve: "smooth", width: 3 },
                fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05, stops: [0, 100] } },
                dataLabels: { enabled: false },
                markers: { size: 0, hover: { size: 5 } },
                xaxis: { categories: monthly.map((m) => moment(m.mes + "-01").format("MMM/YY")), ...ax, axisBorder: { show: false }, axisTicks: { show: false } },
                yaxis: ax }} />
          </div>
        </Grid>

        {/* No-show por médico */}
        <Grid item xs={12} md={6}>
          <div className={c.card}>
            <div className={c.cardHead}><span className={c.cardTitle}>No-show por médico</span></div>
            {(() => {
              const MIN = 8;
              const rows = byDoctor.map((x) => {
                const eleg = x.elegiveis != null ? x.elegiveis : (x.consultas || 0);
                return { ...x, eleg, nome: x.isActive === false ? `${x.medico} (inativo)` : x.medico };
              });
              const plot = rows.filter((x) => x.eleg >= MIN).sort((a, b) => b.noShowPct - a.noShowPct);
              const fracos = rows.filter((x) => x.eleg > 0 && x.eleg < MIN);
              if (plot.length === 0) {
                return <Box display="flex" alignItems="center" justifyContent="center" height={220}><Typography className={c.muted}>Amostra ainda pequena no período.</Typography></Box>;
              }
              return (
                <React.Fragment>
                  <Chart type="bar" height={Math.max(180, plot.length * 50)}
                    series={[{ name: "No-show", data: plot.map((x) => x.noShowPct) }]}
                    options={{ ...baseChart,
                      plotOptions: { bar: { horizontal: true, borderRadius: 6, distributed: true, barHeight: "58%" } },
                      colors: plot.map((x) => (x.noShowPct >= 40 ? "#ef4444" : x.noShowPct >= 25 ? "#f59e0b" : "#10b981")),
                      dataLabels: { enabled: true, formatter: (v, o) => { const r = plot[o.dataPointIndex] || {}; return `${v}% (${r.faltas}/${r.eleg})`; }, style: { fontWeight: 700, colors: ["#fff"] } },
                      legend: { show: false },
                      xaxis: { categories: plot.map((x) => x.nome), labels: { formatter: (v) => `${v}%`, style: { colors: axisColor } } },
                      yaxis: { labels: { style: { colors: axisColor, fontSize: "12.5px", fontWeight: 600 } } },
                      tooltip: { ...baseChart.tooltip, y: { formatter: (v, o) => { const r = plot[o.dataPointIndex] || {}; return `${r.faltas} faltas em ${r.eleg} consultas passadas · cobertura ${r.coberturaPct != null ? r.coberturaPct : "?"}%`; } } } }} />
                  {fracos.length > 0 && (
                    <Typography className={c.muted} style={{ marginTop: 8, display: "block" }}>
                      Amostra pequena (menos de {MIN} consultas), fora do ranking: {fracos.map((x) => `${x.nome} ${x.faltas}/${x.eleg}`).join(" · ")}
                    </Typography>
                  )}
                </React.Fragment>
              );
            })()}
          </div>
        </Grid>

        {/* IA Clara + Tempo de espera */}
        <Grid item xs={12} sm={6} md={3}>
          <div className={c.card} style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <div className={c.kpiTop}><span className={c.kpiLabel}>Marcadas pela IA Clara</span><span className={c.iconChip} style={{ background: "#06b6d422", color: "#06b6d4" }}><Android style={{ fontSize: 19 }} /></span></div>
            <div className={c.hero}>{claraPct}%</div>
            <div className={c.muted}>{claraVal} de {ia.total ?? 0} agendamentos</div>
          </div>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <div className={c.card} style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <div className={c.kpiTop}><span className={c.kpiLabel}>Tempo médio de espera</span><span className={c.iconChip} style={{ background: "#8b5cf622", color: "#8b5cf6" }}><AccessTime style={{ fontSize: 19 }} /></span></div>
            <div className={c.hero}>{wait.esperaMedioMin == null ? "—" : `${wait.esperaMedioMin} min`}</div>
            <div className={c.muted}>{wait.amostra ? `${wait.amostra} consultas medidas` : "dados a partir de hoje"}</div>
          </div>
        </Grid>

        {/* Insights */}
        <Grid item xs={12} md={6}>
          <div className={c.insightCard}>
            <div className={c.cardHead}><EmojiObjects style={{ fontSize: 19, color: "#6366f1" }} /><span className={c.cardTitle}>Insights</span></div>
            {insights.length === 0 ? (
              <Typography className={c.muted}>Sem alertas no período — tudo dentro do esperado. 👌</Typography>
            ) : (
              insights.map((ins, i) => (
                <div key={i} className={c.insight}>
                  {ins.tipo === "alerta" ? <Warning style={{ fontSize: 17, color: "#ef4444", flexShrink: 0, marginTop: 1 }} /> : <TrendingUp style={{ fontSize: 17, color: "#10b981", flexShrink: 0, marginTop: 1 }} />}
                  <span>{ins.texto}</span>
                </div>
              ))
            )}
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

export default GestaoClinica;
